import {
  GraphComponent,
  IRenderContext,
  ObjectRendererBase,
  Rect,
  SvgVisual,
  SvgVisualGroup,
  Visual
} from '@yfiles/yfiles'

/**
 * World-scaled dotted background that pans/zooms with the graph.
 * Also defines the pattern id "dotsBackground" so any ShapeNodeStyle using
 * fill: url(#dotsBackground) (e.g., table columns) can resolve it inside
 * the yFiles SVG render tree.
 */
type WorldBgTag = { kind: 'WORLD_BG' }

class WorldDotsBackground extends ObjectRendererBase<WorldBgTag, SvgVisualGroup> {
  private readonly patternId = 'dotsBackground'

  createVisual(context: IRenderContext, _tag: WorldBgTag): SvgVisualGroup {
    const gc = context.canvasComponent as GraphComponent
    const group = new SvgVisualGroup()

    // Create <defs> with the pattern definition (20x20 tile, 1px dot at 2,2, #aaa)
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern')
    pattern.setAttribute('id', this.patternId)
    pattern.setAttribute('patternUnits', 'userSpaceOnUse')
    pattern.setAttribute('width', '20')
    pattern.setAttribute('height', '20')
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    dot.setAttribute('cx', '2')
    dot.setAttribute('cy', '2')
    dot.setAttribute('r', '1')
    dot.setAttribute('fill', '#aaa')
    pattern.appendChild(dot)
    defs.appendChild(pattern)
    group.add(new SvgVisual(defs))

    // Add the rect that covers the current world viewport
    group.add(this.createWorldRectVisual(gc))

    return group
  }

  updateVisual(context: IRenderContext, oldVisual: Visual | null, _tag: WorldBgTag): SvgVisualGroup {
    const gc = context.canvasComponent as GraphComponent
    const group = oldVisual as SvgVisualGroup

    // child(0) = defs, child(1) = world-covering rect
    group.children.set(1, this.createWorldRectVisual(gc))

    return group
  }

  private createWorldRectVisual(gc: GraphComponent): SvgVisual {
    const worldView: Rect = gc.viewport.getEnlarged(200)
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    rect.setAttribute('x', String(worldView.x))
    rect.setAttribute('y', String(worldView.y))
    rect.setAttribute('width', String(worldView.width))
    rect.setAttribute('height', String(worldView.height))
    rect.setAttribute('fill', `url(#${this.patternId})`)
    rect.setAttribute('pointer-events', 'none')
    return new SvgVisual(rect)
  }
}

export function installWorldBackground(gc: GraphComponent): void {
  // Attach a single background element to the background group so it sits behind everything
  gc.renderTree.createElement(gc.renderTree.backgroundGroup, { kind: 'WORLD_BG' }, new WorldDotsBackground())
}
